import React, { useState, useEffect, useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { Box, IconButton, Paper, Checkbox, TableContainer, Table, TableHead, TableRow, TableCell, TableBody, Dialog, DialogTitle, DialogContent, Button, DialogActions, Typography, Divider, TextField, Tooltip, Menu, MenuItem, CircularProgress } from '@mui/material'
import { useTranslate } from 'src/locales'
import AddIcon from '@mui/icons-material/Add'
import EditIcon from '@mui/icons-material/Edit'
import ByzMuiButton from 'src/components/core-components/mui/buttons/ByzMuiButton'
import ByzMuiTextField from 'src/components/core-components/mui/inputs/ByzMuiTextField'
import PatientDatas from 'src/modules/medcard/patient-datas'
import ByzEditButton from "src/components/core-components/mui/buttons-grid/ByzGrdEditButton";
import IsolationActionModal from './modals/IsolationActionModal';
import { useBaseQuery, useBaseMutationWithVariables } from 'src/api/core/useBaseQuery';
import ByzIsolationSelectListPopupInput from './modals/ByzIsolationSelectListPopupInput';
import { statusLabelMap } from 'src/modules/nurse/isolation/request-list/pages/list/models/enums';
import { ByzMuiDateTimePicker } from 'src/components/core-components/mui/inputs/ByzMuiDateTimePicker';
import { baseService } from 'src/api/core/baseService';
import LoadingScreen from 'src/components/shared-components/loading-screen';
import SelectedServicesPanel from 'src/components/shared-components/selected-services-panel';
import ByzMuiAddButton from 'src/components/core-components/mui/buttons/ByzMuiAddButton';
import ByzMuiPrintButton from 'src/components/core-components/mui/buttons/ByzMuiPrintButton';
import ByzMuiSaveButton from 'src/components/core-components/mui/buttons/ByzMuiSaveButton';
import ByzMuiTypography from 'src/components/core-components/mui/inputs/ByzMuiTypography';
import Grid from '@mui/material/Unstable_Grid2';
import ByzMuiCloseButton from 'src/components/core-components/mui/buttons/ByzMuiCloseButton';
import ByzMuiOpenProcessMenu from "src/components/core-components/mui/ByzMuiOpenProcessMenu";
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import DownloadIcon from '@mui/icons-material/Download';
import DensityMediumIcon from '@mui/icons-material/DensityMedium';
import IsolationRequestList from './components/IsolationRequestList';
import IsolationEditModal from './components/IsolationEditModal';
import IsolationSelectionModal from './components/IsolationSelectionModal';
import ByzMuiSnackBar from 'src/components/core-components/mui/ByzMuiSnackBar';
import ByzCard from 'src/components/core-components/ByzCard';
import { RootState } from 'src/app/store';
import {
  BulkCreateAndUpdateItem,
  BulkUpdateStatusItem,
  IsolationOption,
  IsolationReason,
  IsolationRow,
  IsolationTypeIcon,
  PatientIsolationsResponse,
  UpdateStatusPayload,
} from './models/index';


function IsolationRequest() {
  const { id: rawPatientId } = useParams();
  const { t } = useTranslate();

  // Redux'tan patientId al (hasta bilgi kartından)
  const mainTabs = useSelector((state: RootState) => state.tabs.mainTabs);
  const activeTabIndex = useSelector((state: RootState) => state.tabs.activeTabIndex);
  const activeTab = mainTabs[activeTabIndex];
  const tabPatientId = activeTab?.data?.patientId;

  // Önce URL'den, yoksa Redux'tan al
  const patientIdNumber = rawPatientId ? Number(rawPatientId) : (tabPatientId ? Number(tabPatientId) : null);
  const [addedIsolations, setAddedIsolations] = useState<IsolationRow[]>([]);
  const [isEyeOpen, setIsEyeOpen] = useState<boolean>(true);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [modalOpen, setModalOpen] = useState(false);
  const [modalIsolationName, setModalIsolationName] = useState('');
  const [modalIsolationStatus, setModalIsolationStatus] = useState<string | undefined>(undefined);
  const [selectedIsolationTypeId, setSelectedIsolationTypeId] = useState<number | null>(null);
  const [addButtonDisabledId, setAddButtonDisabledId] = useState<number | null>(null);
  const [pendingIsolations, setPendingIsolations] = useState<IsolationRow[]>([]);
  const [descModalOpen, setDescModalOpen] = useState(false);
  const [descModalValue, setDescModalValue] = useState('');
  const [descModalIdx, setDescModalIdx] = useState<number | null>(null);
  const [descModalField, setDescModalField] =
    useState<'isolation_taken_descrption' | 'isolation_without_bact_descrp' | null>(null);
  const [descModalIsPending, setDescModalIsPending] = useState<boolean>(false);
  const [editRowModalOpen, setEditRowModalOpen] = useState(false);
  const [editRowIsPending, setEditRowIsPending] = useState(false);
  const [editRowIndex, setEditRowIndex] = useState<number | null>(null);
  const [editRowData, setEditRowData] = useState<IsolationRow | null>(null);
  const [modalIsolationRow, setModalIsolationRow] = useState<IsolationRow | null>(null);
  const [selectionDialogOpen, setSelectionDialogOpen] = useState(false);
  const [isModalSelectionsExpanded, setIsModalSelectionsExpanded] = useState<boolean>(true);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarTitle, setSnackbarTitle] = useState('');
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarType, setSnackbarType] = useState<'success' | 'error'>('success');

  // Modal üst grid toolbar state'leri
  const [columnsMenuAnchorEl, setColumnsMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [densityMenuAnchorEl, setDensityMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [rowDensity, setRowDensity] = useState<'compact' | 'standard' | 'comfortable'>('standard');
  const [columnVisibility, setColumnVisibility] = useState({
    icon: true,
    name: true,
    description: true,
    handHygiene: true,
    gloves: true,
    gown: true,
    mask: true,
    eyeProtection: true,
    patientTransfer: true,
  });

  const {
    mutate: createAndUpdateIsolationRequests,
    isPending: isSavingPendingIsolations,
  } = useBaseMutationWithVariables<unknown, { items: BulkCreateAndUpdateItem[] }>({
    endpoint: 'isolation/api/PatientIsolations/CreateAndUpdatePatientIsolationRequests',
    method: 'POST',
  });

  const {
    mutate: updateIsolationRequestStatus,
    isPending: isUpdatingStatus,
  } = useBaseMutationWithVariables<unknown, UpdateStatusPayload>({
    endpoint: 'isolation/api/PatientIsolations/UpdateIsolationRequestStatus',
    method: 'PUT',
  });

  const {
    mutate: bulkUpdateIsolationRequestStatus,
    isPending: isBulkUpdatingStatus,
  } = useBaseMutationWithVariables<unknown, { items: BulkUpdateStatusItem[] }>({
    endpoint: 'isolation/api/PatientIsolations/BulkUpdateIsolationRequestStatus',
    method: 'PUT',
  });

  const handleCommitPendingIsolations = () => {
    if (!pendingIsolations.length) {
      return;
    }

    if (!patientIdNumber || Number.isNaN(patientIdNumber) || patientIdNumber <= 0) {
      setSnackbarTitle(t('common.error') ?? 'Hata');
      setSnackbarMessage(t('isolation-requests.errors.no-patient') ?? 'Hasta bilgisi bulunamadı. Lütfen sayfayı hasta kartı üzerinden açın.');
      setSnackbarType('error');
      setSnackbarOpen(true);
      return;
    }

    const items: BulkCreateAndUpdateItem[] = pendingIsolations.map((item) => ({
      id: 0,
      ptid: patientIdNumber,
      isolationtypeid: item.ist_id ?? 0,
      isolationtakenreason: item.isolation_taken_reason ?? null,
      isolationtakendescription: item.isolation_taken_descrption ?? null,
      isolationwithoutbacteria: item.isolation_without_bacteria ?? null,
      isolationwithoutbacteriadescription: item.isolation_without_bact_descrp ?? null,
      isgivenfamilyeducation: item.is_given_family_education ?? null,
      notificationlab: item.notification_lab ?? null,
      notificationkitchen: item.notification_kitchen ?? null,
      notificationcleaner: item.notification_cleaner ?? null,
      startdate: item.start_date ?? null,
      approvestatus: item.approve_status ?? "0",
    }));

    createAndUpdateIsolationRequests(
      { items },
      {
        onSuccess: () => {
          setPendingIsolations([]);
          refetchPatientIsolations();
          setSelectionDialogOpen(false);
        },
        onError: () => {
          setSnackbarTitle(t('common.error') ?? 'Hata');
          setSnackbarMessage(t('isolation-requests.errors.save-failed') ?? 'İzolasyonlar kaydedilirken hata oluştu!');
          setSnackbarType('error');
          setSnackbarOpen(true);
          refetchPatientIsolations();
        },
      }
    );
  };


  const { data: isolationTypeData, isLoading: isIsolationTypeLoading } = useBaseQuery<{ data: IsolationOption[] }>({
    endpoint: 'isolation/api/IsolationType',
    queryKey: ['IsolationType'],
    options: {
      enabled: !!patientIdNumber && patientIdNumber > 0,
    },
  });
  const { data: isolationTypeIconsData, isLoading: isIsolationTypeIconsLoading } = useBaseQuery<{ data: IsolationTypeIcon[] }>({
    endpoint: 'isolation/api/IsolationType/GetIsolationTypeIcons',
    queryKey: ['IsolationTypeIcons'],
    options: {
      staleTime: 1000 * 60 * 60 * 24, // 24saat cache
    }
  });

  const { data: patientIsolationsData, refetch: refetchPatientIsolations, isLoading: isPatientIsolationsLoading } =
    useBaseQuery<{ data: PatientIsolationsResponse }>({
      endpoint: patientIdNumber && patientIdNumber > 0
        ? `isolation/api/PatientIsolations/GetPatientAllIsolationsByPtId?ptId=${patientIdNumber}`
        : "",
      queryKey: ['PatientAllIsolations', patientIdNumber],
      options: {
        enabled: !!patientIdNumber && patientIdNumber > 0,
      },
    });
  const patientIsolations: IsolationRow[] = Array.isArray(patientIsolationsData?.data?.result)
    ? (patientIsolationsData?.data?.result as IsolationRow[])
    : [];

  // Lookup verilerini çek
  const { data: isolationReasonsData, isLoading: isIsolationReasonsLoading } = useBaseQuery<{ data: IsolationReason[] }>({
    endpoint: 'isolation/api/IsolationTakenReason/GetAll',
    queryKey: ['IsolationTakenReason'],
    options: {
      enabled: !!patientIdNumber && patientIdNumber > 0,
    },
  });
  const { data: nonBacterialReasonsData, isLoading: isNonBacterialReasonsLoading } = useBaseQuery<{ data: IsolationReason[] }>({
    endpoint: 'isolation/api/IsolationTakenReasonWithoutBacteria/GetAll',
    queryKey: ['IsolationTakenReasonWithoutBacteria'],
    options: {
      enabled: !!patientIdNumber && patientIdNumber > 0,
    },
  });

  // Tüm veriler yüklenene kadar loading göster
  const isAllDataLoading = isIsolationTypeLoading || isIsolationTypeIconsLoading || isPatientIsolationsLoading || isIsolationReasonsLoading || isNonBacterialReasonsLoading;

  const isolationReasons: IsolationReason[] = isolationReasonsData && Array.isArray(isolationReasonsData.data) ? isolationReasonsData.data : [];
  const nonBacterialReasons: IsolationReason[] = nonBacterialReasonsData && Array.isArray(nonBacterialReasonsData.data) ? nonBacterialReasonsData.data : [];

  const isolationTypeIconsMap = useMemo(() => {
    const rawIcons = isolationTypeIconsData?.data;
    const iconsArr: IsolationTypeIcon[] = Array.isArray(rawIcons) ? rawIcons : [];
    const map: Record<number, IsolationTypeIcon> = {};
    iconsArr.forEach((icon) => {
      if (icon?.id) {
        map[Number(icon.id)] = icon;
      }
    });
    return map;
  }, [isolationTypeIconsData]);

  useEffect(() => {
    if (patientIsolations.length > 0) {
      const mapped = patientIsolations.map((item) => ({
        ...item,
        isolation_taken_reason_label: isolationReasons.find((r) => r.id === item.isolation_taken_reason)?.name || "",
        isolation_without_bacteria_label: nonBacterialReasons.find((r) => r.id === item.isolation_without_bacteria)?.name || "",
      }));
      setAddedIsolations(mapped);
    }
  }, [patientIsolations, isolationReasons, nonBacterialReasons]);

  // Grid'deki "Beklemede" durumundaki izolasyonları filtrele
  const pendingStatusIsolations = addedIsolations.filter((item) => String(item.approve_status ?? "0") === "0");

  const rowHeight =
    rowDensity === 'compact' ? 32 : rowDensity === 'comfortable' ? 56 : 40;

  const handleToggleColumnVisibility = (key: keyof typeof columnVisibility) => {
    setColumnVisibility((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleChangeDensity = (value: 'compact' | 'standard' | 'comfortable') => {
    setRowDensity(value);
    setDensityMenuAnchorEl(null);
  };

  const handleExportCsv = () => {
    const headers = [
      'Adı',
      'Açıklama',
      'El Hijyeni',
      'Eldiven',
      'Önlük',
      'Maske',
      'Gözlük',
      'Hastanın Transferi',
    ];

    const rows = filteredOptions.map((option) => [
      option.name,
      option.descrption,
      option.hand_hygiene ?? option.elHijyeni,
      option.glove ?? option.eldiven,
      option.bib ?? option.onluk,
      option.mask ?? option.maske,
      option.glasses ?? option.gozluk,
      option.transfer_of_patient ?? option.hastaninTransferi,
    ]);

    const csvContent = [headers, ...rows]
      .map((r) => r.map((v) => `"${String(v ?? '').replace(/"/g, '""')}"`).join(';'))
      .join('\r\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'izolasyon_listesi.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Toplu gönderme fonksiyonu
  const handleBulkSendPendingIsolations = () => {
    if (pendingStatusIsolations.length === 0) {
      return;
    }

    const items: BulkUpdateStatusItem[] = pendingStatusIsolations.map((item) => {
      const isolationId = item.pieid ?? item.id;
      return {
        pieid: Number(isolationId),
        status: "1", // Gönderildi
      };
    });

    // Optimistic update - UI'ı hemen güncelle
    setAddedIsolations(prev =>
      prev.map(item => {
        const itemId = item.pieid ?? item.id;
        const isPending = pendingStatusIsolations.some(
          (pending) => (pending.pieid ?? pending.id) === itemId
        );
        return isPending ? { ...item, approve_status: "1" } : item;
      })
    );

    // Backend'e istek at
    bulkUpdateIsolationRequestStatus(
      { items },
      {
        onSuccess: () => {
          refetchPatientIsolations();
        },
        onError: (error) => {
          // Hata olursa eski haline geri al
          refetchPatientIsolations();
          setSnackbarTitle(t('common.error') ?? 'Hata');
          setSnackbarMessage(t('isolation-requests.errors.bulk-send-failed') ?? 'Toplu gönderme sırasında hata oluştu!');
          setSnackbarType('error');
          setSnackbarOpen(true);
        },
      }
    );
  };

  const getImageSrc = (base64: string | undefined | null) => {
    if (!base64) return "";

    // Base64 çözümlenmiş ilk birkaç karakter kontrol edilir
    const decodedStart = atob(base64).slice(0, 50).toLowerCase();

    if (decodedStart.includes("<svg")) {
      return `data:image/svg+xml;base64,${base64}`;
    } else if (base64.startsWith("iVBORw0KGgo")) {
      return `data:image/png;base64,${base64}`;
    } else {
      return `data:image/*;base64,${base64}`;
    }
  };

  const isolationOptions: IsolationOption[] = useMemo(() => {
    const base = (Array.isArray(isolationTypeData?.data) ? isolationTypeData?.data : []) ?? [];
    return base.map((item) => {
      const iconKey = (item?.id ?? item?.isolationtypeid) ?? undefined;
      const iconObj = iconKey ? isolationTypeIconsMap[iconKey] : undefined;
      return {
        ...item,
        pics_svg: item?.pics_svg ?? iconObj?.pics_svg ?? null,
      };
    });
  }, [isolationTypeData, isolationTypeIconsMap]);

  const filteredOptions = isolationOptions.filter((option) =>
    option.is_active === "1" && (
      option.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      option.descrption?.toLowerCase().includes(searchTerm.toLowerCase())
    )
  );


  const handleAddSingle = (id: number) => {
    setAddButtonDisabledId(id);

    const option = isolationOptions.find((o) => o.id === id);
    if (!option) {
      setAddButtonDisabledId(null);
      return;
    }

    setPendingIsolations((prev) => {
      if (prev.some((p) => p.ist_id === id)) {
        return prev;
      }

      const tempId = `temp-${id}-${Date.now()}`;

      const newItem: IsolationRow = {
        id: tempId,
        ist_id: id,
        isolationtypename: option.name,
        icon: option.pics_svg,
        approve_status: "0",
        isolation_taken_reason: null,
        isolation_taken_descrption: "",
        isolation_without_bacteria: null,
        isolation_without_bact_descrp: "",
        is_given_family_education: "0",
        notification_lab: "0",
        notification_kitchen: "0",
        notification_cleaner: "0",
        start_date: null,
        finish_date: null,
        app_or_reject_date: null,
        isolation_taken_reason_label: "",
        isolation_without_bacteria_label: "",
      };

      return [...prev, newItem];
    });

    setAddButtonDisabledId(null);
  };

  const handleEditClick = (isolationName: string, status?: string, rowObj?: IsolationRow) => {
    setModalIsolationName(isolationName);
    setModalIsolationStatus(status);
    setModalIsolationRow(rowObj ?? null);
    setModalOpen(true);
  };

  const handleModalClose = () => {
    setModalOpen(false);
  };

  const handleOpenSelectionDialog = () => {
    setSelectionDialogOpen(true);
  };

  const handleCloseSelectionDialog = () => {
    setSelectionDialogOpen(false);
  };


  const handleDeletePatientIsolation = async (id: number) => {
    // Optimistic: ekrandan hemen kaldır
    setAddedIsolations((prev) =>
      prev.filter((item) => (item.pieid ?? item.id) !== id)
    );
    setPendingIsolations((prev) =>
      prev.filter((item) => (item.pieid ?? item.id ?? item.ist_id) !== id)
    );

    try {
      await baseService.delete("isolation/api/PatientIsolations/DeletePatientIsolationRequest/", id);
      refetchPatientIsolations();
    } catch (err) {
      setSnackbarTitle(t('common.error') ?? 'Hata');
      setSnackbarMessage(t('isolation-requests.errors.delete-failed') ?? 'Silme sırasında hata oluştu!');
      setSnackbarType('error');
      setSnackbarOpen(true);
    }
  };


  const handleUpdateIsolation = (_isolation: IsolationRow) => {
    // Bu ekranda tekil UpdatePatientIsolationRequest endpoint'ini çağırmıyoruz.
    // Değişiklikler sadece local state'te tutuluyor; gerçek persist hemşire listesi ekranındaki
    // bulk status/update akışından veya backend'in bulk create&update endpoint'inden yapılıyor.
  };

  const handleModalAction = (action: 'gonder' | 'bitir' | 'gerial' | 'sil', newStatus?: string) => {
    if (!modalIsolationRow) {
      setModalOpen(false);
      return;
    }

    // Sil işlemi
    if (action === 'sil') {
      const isolationId = modalIsolationRow.pieid ?? modalIsolationRow.id;
      if (isolationId) {
        handleDeletePatientIsolation(Number(isolationId));
      }
      setModalOpen(false);
      return;
    }

    // Status güncelleme işlemleri (Gönder, Geri Al, Bitir)
    if (newStatus) {
      const isolationId = modalIsolationRow.pieid ?? modalIsolationRow.id;
      if (!isolationId) {
        setSnackbarTitle(t('common.error') ?? 'Hata');
        setSnackbarMessage(t('isolation-requests.errors.not-found') ?? 'İzolasyon ID bulunamadı!');
        setSnackbarType('error');
        setSnackbarOpen(true);
        setModalOpen(false);
        return;
      }

      // Optimistic update - UI'ı hemen güncelle
      setAddedIsolations(prev =>
        prev.map(item => {
          const itemId = item.pieid ?? item.id;
          return itemId === isolationId
            ? { ...item, approve_status: newStatus }
            : item;
        })
      );

      // Backend'e istek at
      updateIsolationRequestStatus(
        {
          pieid: Number(isolationId),
          status: newStatus,
        },
        {
          onSuccess: () => {
            // Başarılı olursa veriyi yeniden çek
            refetchPatientIsolations();
          },
          onError: (error) => {
            // Hata olursa eski haline geri al
            refetchPatientIsolations();
            setSnackbarTitle(t('common.error') ?? 'Hata');
            setSnackbarMessage(t('isolation-requests.errors.status-update-failed') ?? 'Durum güncellenirken hata oluştu!');
            setSnackbarType('error');
            setSnackbarOpen(true);
          },
        }
      );
    }

    setModalOpen(false);
  };



  const renderSelectionsTable = (rowsOverride?: IsolationRow[], maxHeight?: string | number) => {
    const rows = rowsOverride ?? addedIsolations;
    const isPendingTable = rowsOverride === pendingIsolations;
    const resolvedMaxHeight = maxHeight ?? "500px";
    return (
      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          boxShadow: "0 2px 8px 0 rgba(60,72,88,0.04)",
          maxHeight: resolvedMaxHeight,
          overflow: "auto",
        }}
      >
        {isPatientIsolationsLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
            <LoadingScreen />
          </Box>
        ) : (
          <Table sx={{ borderCollapse: 'collapse' }}>
            <TableHead
              style={{ display: isEyeOpen ? 'table-header-group' : 'none' }}
              sx={{
                '& th': {
                  fontFamily: 'Poppins, sans-serif',
                  fontWeight: 700,
                  fontSize: '12px',
                  lineHeight: '24px',
                  letterSpacing: '0.17px',
                  backgroundColor: '#F4F7FE',
                  color: '#000000',
                  border: '1px solid #E0E0E0',
                  textAlign: 'center',
                },
              }}
            >
              <TableRow>
                {/* En solda checkbox kolonu için sadece üst başlık kutusu (checkbox ikinci satırda) */}
                <TableCell
                  padding="checkbox"
                  sx={{
                    py: 1,
                    px: 1,
                    width: 40,
                  }}
                />
                {/* Üst satırda ikon + ad + durum için grup başlığı: İzolasyon */}
                <TableCell
                  colSpan={2}
                  sx={{
                    py: 1,
                    px: 2,
                    width: '150px',
                  }}
                >
                  {t("isolation-requests.grid-columns.isolation")}
                </TableCell>
                <TableCell
                  colSpan={2}
                  sx={{
                    py: 1,
                    px: 2,
                  }}
                >
                  {t("isolation-requests.form.isolation-take-reason")}
                </TableCell>
                <TableCell
                  colSpan={2}
                  sx={{
                    py: 1,
                    px: 2,
                  }}
                >
                  {t("isolation-requests.form.non-bacterial-isolation-reason")}
                </TableCell>
                <TableCell
                  colSpan={3}
                  sx={{
                    py: 1,
                    px: 2,
                  }}
                >
                  {t("isolation-requests.form.isolation-notification")}
                </TableCell>
                <TableCell
                  colSpan={5}
                  sx={{
                    py: 1,
                    px: 2,
                  }}
                >
                  {t("isolation-requests.form.isolation-entry")}
                </TableCell>
              </TableRow>
              <TableRow>
                {/* Checkbox kolonu: ikinci satırda header hücresi ve toplu seçim */}
                <TableCell
                  padding="checkbox"
                >
                  {isPendingTable ? (
                    <Checkbox
                      color="primary"
                      size="small"
                      checked={rows.length > 0}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        if (!checked) {
                          setPendingIsolations([]);
                        }
                      }}
                    />
                  ) : (
                    <Checkbox
                      color="primary"
                      size="small"
                      checked={false}
                      disabled
                    />
                  )}
                </TableCell>
                <TableCell>
                  {t("isolation-requests.isolation-selection.columns.icon")}
                </TableCell>
                <TableCell>
                  {t("isolation-requests.isolation-selection.columns.isolation-name")}
                </TableCell>
                {/* Durum kolonu header'ı kaldırıldı, bilgi en sonda gösterilecek */}
                <TableCell>
                  {t("isolation-requests.form.isolation-reason")}
                </TableCell>
                <TableCell>
                  {t("isolation-requests.form.description")}
                </TableCell>
                <TableCell>
                  {t("isolation-requests.form.isolation-reason")}
                </TableCell>
                <TableCell>
                  {t("isolation-requests.form.description")}
                </TableCell>
                <TableCell>
                  {t("isolation-requests.form.microbiology")}
                </TableCell>
                <TableCell>
                  {t("isolation-requests.form.kitchen-team")}
                </TableCell>
                <TableCell>
                  {t("isolation-requests.form.cleaning-team")}
                </TableCell>
                <TableCell>
                  {t("isolation-requests.start-date")}
                </TableCell>
                <TableCell>
                  {t("isolation-requests.end-date")}
                </TableCell>
                <TableCell>
                  {t("isolation-requests.form.patient-and-family")}
                </TableCell>
                {/* Onay / Ret yerine Durumu başlığı */}
                <TableCell>
                  {t("isolation-requests.status")}
                </TableCell>
                <TableCell>
                  {t("isolation-requests.edit")}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((option, idx) => {
                const approveKey = String(option.approve_status ?? "0") as keyof typeof statusLabelMap;
                const isRowDisabled = statusLabelMap[approveKey] !== "Beklemede";
                return (
                  <TableRow key={`selected-${option.id}`} sx={{ height: 70, '& > td': { verticalAlign: 'middle', textAlign: 'center' } }}>
                    {/* Satır checkbox kolonu */}
                    <TableCell padding="checkbox" sx={{ py: 0.5, px: 1, textAlign: 'center' }}>
                      {isPendingTable ? (
                        <Checkbox
                          color="primary"
                          size="small"
                          checked
                          onChange={() => {
                            setPendingIsolations((prev) =>
                              prev.filter((item) => item.id !== option.id)
                            );
                          }}
                        />
                      ) : (
                        <Checkbox
                          color="primary"
                          size="small"
                          checked
                          disabled
                        />
                      )}
                    </TableCell>
                    <TableCell sx={{ py: 0.5, px: 1 }}>
                      {option.icon && (
                        <img
                          src={getImageSrc(option.icon)}
                          alt={option.name}
                          style={{ width: 32, height: 32, display: 'block', margin: '0 auto' }}
                        />
                      )}
                    </TableCell>
                    <TableCell sx={{ py: 0.5, px: 1 }}>
                      {option.isolationtypename}
                    </TableCell>
                    {/* İzolasyon Nedeni */}
                    <TableCell sx={{ py: 0.5, px: 1 }}>
                      {option.isolation_taken_reason_label || "-"}
                    </TableCell>
                    {/* İzolasyon Nedeni Açıklama */}
                    <TableCell sx={{ py: 0.5, px: 1 }}>
                      {option.isolation_taken_descrption || "-"}
                    </TableCell>
                    {/* Bakteri Dışı İzolasyon Nedeni */}
                    <TableCell sx={{ py: 0.5, px: 1 }}>
                      {option.isolation_without_bacteria_label || "-"}
                    </TableCell>
                    {/* Bakteri Dışı Açıklama */}
                    <TableCell sx={{ py: 0.5, px: 1 }}>
                      {option.isolation_without_bact_descrp || "-"}
                    </TableCell>
                    {/* Mikrobiyoloji */}
                    <TableCell sx={{ py: 0.5, px: 1 }}>
                      {option.notification_lab === "1" ? t("isolation-requests.common-yes") || "Evet" : t("isolation-requests.common-no") || "-"}
                    </TableCell>
                    {/* Mutfak Ekibi */}
                    <TableCell sx={{ py: 0.5, px: 1 }}>
                      {option.notification_kitchen === "1" ? t("isolation-requests.common-yes") || "Evet" : t("isolation-requests.common-no") || "-"}
                    </TableCell>
                    {/* Temizlik Ekibi */}
                    <TableCell sx={{ py: 0.5, px: 1 }}>
                      {option.notification_cleaner === "1" ? t("isolation-requests.common-yes") || "Evet" : t("isolation-requests.common-no") || "-"}
                    </TableCell>
                    {/* Başlangıç Tarihi */}
                    <TableCell sx={{ py: 0.5, px: 1 }}>
                      {option.start_date
                        ? new Date(option.start_date).toLocaleString()
                        : "-"}
                    </TableCell>
                    {/* Bitiş Tarihi */}
                    <TableCell sx={{ py: 0.5, px: 1 }}>
                      {option.finish_date
                        ? new Date(option.finish_date).toLocaleString()
                        : "-"}
                    </TableCell>
                    {/* Hasta ve Ailesi */}
                    <TableCell sx={{ py: 0.5, px: 1 }}>
                      {option.is_given_family_education === "1"
                        ? t("isolation-requests.common-yes") || "Evet"
                        : t("isolation-requests.common-no") || "-"}
                    </TableCell>
                    {/* Durumu - ekleme modalinde her zaman Beklemede (veya gerçek status) */}
                    <TableCell
                      sx={{
                        py: 0.5,
                        px: 1,
                        backgroundColor: (() => {
                          const status = isPendingTable || !option.approve_status ? "0" : option.approve_status;
                          return status === "0"
                            ? "#FFCC004D" // Beklemede
                            : status === "1"
                              ? "#AEADA64D" // Gönderildi
                              : status === "2"
                                ? "#34C7594D" // Onaylandı
                                : "transparent";
                        })(),
                        fontWeight: 700,
                      }}
                    >
                      {(() => {
                        const status = isPendingTable || !option.approve_status ? "0" : option.approve_status;
                        return status === "0"
                          ? t("isolation-requests.status-labels.pending")
                          : status === "1"
                            ? t("isolation-requests.status-labels.sent")
                            : status === "2"
                              ? t("isolation-requests.status-labels.approved")
                              : status === "3"
                                ? t("isolation-requests.status-labels.cancelled")
                                : status === "4"
                                  ? t("isolation-requests.status-labels.completed")
                                  : "";
                      })()}
                    </TableCell>
                    <TableCell sx={{ py: 0.5, px: 1 }}>
                      <ByzEditButton
                        size="small"
                        onClick={() => {
                          setEditRowIsPending(isPendingTable);
                          setEditRowIndex(idx);
                          setEditRowData(option);
                          setEditRowModalOpen(true);
                        }}
                        disabled={option.approve_status === '4' || option.approve_status === '3'}
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </TableContainer>
    );
  };

  const renderSelectionsContent = (
    state: { isExpanded: boolean; isFullScreen: boolean },
    options?: { rows?: IsolationRow[]; maxHeight?: string | number }
  ) => {
    if (!state.isExpanded) {
      return null;
    }

    const computedMaxHeight =
      options?.maxHeight ??
      (state.isFullScreen ? "calc(80vh - 220px)" : "500px");

    return (
      <Box
        sx={{
          flex: 1,
          width: "100%",
          display: "flex",
          flexDirection: "column",
          p: 2,
          pt: 1,
          overflow: "hidden",
        }}
      >
        {renderSelectionsTable(options?.rows, computedMaxHeight)}
      </Box>
    );
  };

  return (
    <Box sx={{ width: '100%', p: 1, background: 'transparent', position: 'relative' }}>
      {isBulkUpdatingStatus && (
        <Box
          sx={{
            position: 'fixed',
            inset: 0,
            zIndex: 2000,
            bgcolor: 'rgba(255,255,255,0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <LoadingScreen />
        </Box>
      )}
      <PatientDatas isCreateMedCard={true} isOpenMedCard={true} />

      {/* Hasta kartının altında izolasyon aksiyon paneli */}
      <ByzCard title={t("isolation-requests.title")} sx={{ mt: 1.5, mb: 1.5 }}>

        {/* Sütunlar menüsü */}
        <Menu
          anchorEl={columnsMenuAnchorEl}
          open={Boolean(columnsMenuAnchorEl)}
          onClose={() => setColumnsMenuAnchorEl(null)}
        >
          <MenuItem
            dense
            onClick={() => handleToggleColumnVisibility('icon')}
          >
            <Checkbox
              size="small"
              checked={columnVisibility.icon}
              sx={{ mr: 1 }}
            />
            {t("isolation-requests.isolation-selection.columns.icon")}
          </MenuItem>
          <MenuItem
            dense
            onClick={() => handleToggleColumnVisibility('name')}
          >
            <Checkbox
              size="small"
              checked={columnVisibility.name}
              sx={{ mr: 1 }}
            />
            {t("isolation-requests.isolation-selection.columns.isolation-name")}
          </MenuItem>
          <MenuItem
            dense
            onClick={() => handleToggleColumnVisibility('description')}
          >
            <Checkbox
              size="small"
              checked={columnVisibility.description}
              sx={{ mr: 1 }}
            />
            {t("isolation-requests.isolation-selection.columns.description")}
          </MenuItem>
          <MenuItem
            dense
            onClick={() => handleToggleColumnVisibility('handHygiene')}
          >
            <Checkbox
              size="small"
              checked={columnVisibility.handHygiene}
              sx={{ mr: 1 }}
            />
            {t("isolation-requests.isolation-selection.columns.hand-hygiene")}
          </MenuItem>
          <MenuItem
            dense
            onClick={() => handleToggleColumnVisibility('gloves')}
          >
            <Checkbox
              size="small"
              checked={columnVisibility.gloves}
              sx={{ mr: 1 }}
            />
            {t("isolation-requests.isolation-selection.columns.gloves")}
          </MenuItem>
          <MenuItem
            dense
            onClick={() => handleToggleColumnVisibility('gown')}
          >
            <Checkbox
              size="small"
              checked={columnVisibility.gown}
              sx={{ mr: 1 }}
            />
            {t("isolation-requests.isolation-selection.columns.gown")}
          </MenuItem>
          <MenuItem
            dense
            onClick={() => handleToggleColumnVisibility('mask')}
          >
            <Checkbox
              size="small"
              checked={columnVisibility.mask}
              sx={{ mr: 1 }}
            />
            {t("isolation-requests.isolation-selection.columns.mask")}
          </MenuItem>
          <MenuItem
            dense
            onClick={() => handleToggleColumnVisibility('eyeProtection')}
          >
            <Checkbox
              size="small"
              checked={columnVisibility.eyeProtection}
              sx={{ mr: 1 }}
            />
            {t("isolation-requests.isolation-selection.columns.eye-protection")}
          </MenuItem>
          <MenuItem
            dense
            onClick={() => handleToggleColumnVisibility('patientTransfer')}
          >
            <Checkbox
              size="small"
              checked={columnVisibility.patientTransfer}
              sx={{ mr: 1 }}
            />
            {t("isolation-requests.isolation-selection.columns.patient-transfer")}
          </MenuItem>
        </Menu>

        {/* Satır aralığı (density) menüsü */}
        <Menu
          anchorEl={densityMenuAnchorEl}
          open={Boolean(densityMenuAnchorEl)}
          onClose={() => setDensityMenuAnchorEl(null)}
        >
          <MenuItem
            selected={rowDensity === 'compact'}
            onClick={() => handleChangeDensity('compact')}
          >
            Sıkı
          </MenuItem>
          <MenuItem
            selected={rowDensity === 'standard'}
            onClick={() => handleChangeDensity('standard')}
          >
            Normal
          </MenuItem>
          <MenuItem
            selected={rowDensity === 'comfortable'}
            onClick={() => handleChangeDensity('comfortable')}
          >
            Geniş
          </MenuItem>
        </Menu>

        {/* Alt: sol Ekle/Gönder, sağ ikonlar */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          {/* Sol taraf: butonlar */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <ByzMuiAddButton onClick={handleOpenSelectionDialog} />
            <ByzMuiButton
              variant="outlined"
              size="small"
              onClick={handleBulkSendPendingIsolations}
              disabled={isBulkUpdatingStatus || pendingStatusIsolations.length === 0}
            >
              {isBulkUpdatingStatus && (
                <CircularProgress size={16} color="inherit" sx={{ mr: 1 }} />
              )}
              {t("buttons.send")}
            </ByzMuiButton>
          </Box>

          {/* Sağ taraf: ikonlar (ByzMuiPrintButton type 1-2-3) */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <ByzMuiPrintButton
              type={1}
              disabled
              sx={{ width: 50, height: 50, borderRadius: "50%" }}
            />
            <ByzMuiPrintButton
              type={2}
              disabled
              sx={{ width: 50, height: 50, borderRadius: "50%" }}
            />
            <ByzMuiPrintButton
              type={3}
              disabled
              sx={{ width: 50, height: 50, borderRadius: "50%" }}
            />
          </Box>
        </Box>
      </ByzCard>

      <IsolationRequestList
        rows={addedIsolations}
        getImageSrc={getImageSrc}
        onRowActionClick={(item: IsolationRow) => {
          setModalIsolationName(item.isolationtypename ?? "");
          setModalIsolationStatus(item.approve_status ?? undefined);
          setModalIsolationRow(item);
          setModalOpen(true);
        }}
        onEditClick={(item: IsolationRow) => {
          handleEditClick(item.isolationtypename ?? "", item.approve_status ?? undefined, item);
        }}
      />
      <IsolationActionModal
        open={modalOpen}
        onClose={handleModalClose}
        isolationName={modalIsolationName}
        onAction={handleModalAction}
        status={modalIsolationStatus}
      />
      <IsolationEditModal
        open={editRowModalOpen}
        editRowData={editRowData}
        editRowIndex={editRowIndex}
        editRowIsPending={editRowIsPending}
        onClose={() => {
          setEditRowModalOpen(false);
          setEditRowData(null);
          setEditRowIndex(null);
          setEditRowIsPending(false);
        }}
        onSave={({
          editRowData: updatedData,
          editRowIndex: index,
          editRowIsPending: isPending,
        }: {
          editRowData: IsolationRow;
          editRowIndex: number | null;
          editRowIsPending: boolean;
        }) => {
          if (index === null || !updatedData) {
            setEditRowModalOpen(false);
            return;
          }

          if (isPending) {
            setPendingIsolations((prev) =>
              prev.map((item, i) =>
                i === index ? { ...item, ...updatedData } : item
              )
            );
          } else {
            setAddedIsolations((prev) =>
              prev.map((item, i) =>
                i === index ? { ...item, ...updatedData } : item
              )
            );
            handleUpdateIsolation(updatedData);
          }
          setEditRowModalOpen(false);
          setEditRowData(null);
          setEditRowIndex(null);
          setEditRowIsPending(false);
        }}
        setEditRowData={setEditRowData}
      />

      {/* Açıklama düzenleme modalı */}
      <Dialog open={descModalOpen} onClose={() => setDescModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Açıklama Düzenle</DialogTitle>
        <DialogContent>
          <ByzMuiTextField
            value={descModalValue}
            onChange={e => setDescModalValue(e.target.value)}
            multiline
            minRows={4}
            fullWidth
            autoFocus
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setDescModalOpen(false); setDescModalIsPending(false); }}>İptal</Button>
          <Button
            onClick={() => {
              if (descModalIdx !== null && descModalField) {
                if (descModalIsPending) {
                  // Sadece pending satırda local güncelle
                  setPendingIsolations(prev =>
                    prev.map((item, i) =>
                      i === descModalIdx ? { ...item, [descModalField]: descModalValue } : item
                    )
                  );
                } else {
                  // Gerçek kayıtlar için hem state hem backend update
                  setAddedIsolations(prev =>
                    prev.map((item, i) =>
                      i === descModalIdx ? { ...item, [descModalField]: descModalValue } : item
                    )
                  );
                  handleUpdateIsolation({
                    ...addedIsolations[descModalIdx],
                    [descModalField]: descModalValue
                  });
                }
              }
              setDescModalOpen(false);
              setDescModalIsPending(false);
            }}
            variant="contained"
          >
            Kaydet
          </Button>
        </DialogActions>
      </Dialog>

      <IsolationSelectionModal
        open={selectionDialogOpen}
        onClose={handleCloseSelectionDialog}
        isIsolationTypeLoading={isIsolationTypeLoading}
        isPatientIsolationsLoading={isPatientIsolationsLoading}
        isSavingPendingIsolations={isSavingPendingIsolations}
        isolationOptions={isolationOptions}
        addedIsolations={addedIsolations}
        pendingIsolations={pendingIsolations}
        setPendingIsolations={setPendingIsolations}
        getImageSrc={getImageSrc}
        handleCommitPendingIsolations={handleCommitPendingIsolations}
        onEditPendingIsolation={(row: IsolationRow, index: number) => {
          setEditRowIsPending(true);
          setEditRowIndex(index);
          setEditRowData(row);
          setEditRowModalOpen(true);
        }}
      />
      <ByzMuiSnackBar
        open={snackbarOpen}
        onClose={() => setSnackbarOpen(false)}
        title={snackbarTitle}
        message={snackbarMessage}
        type={snackbarType}
      />
    </Box>
  );
}

export default IsolationRequest;
